import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, Plus, RefreshCw, Clock, Copy, Inbox, Settings, X, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TempEmail() {
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [prefix, setPrefix] = useState("");
  const [settings, setSettings] = useState({
    smtp_host: "",
    smtp_port: 587,
    imap_host: "",
    imap_port: 993,
    domain: "",
    username: "",
    password: ""
  });
  const userRole = localStorage.getItem("role");
  const isAdmin = userRole === "admin";

  const fetchEmails = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/temp-email/my-emails`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEmails(data);
      }
    } catch (err) {
      toast.error("Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  };

  const fetchInbox = async (emailId) => {
    setInboxLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/temp-email/${emailId}/inbox`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInbox(data);
      }
    } catch (err) {
      toast.error("Ошибка загрузки входящих");
    } finally {
      setInboxLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/temp-email/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data && Object.keys(data).length > 0) {
          setSettings(prev => ({ ...prev, ...data }));
        }
      }
    } catch (err) {
      console.error("Error fetching settings:", err);
    }
  };

  useEffect(() => {
    fetchEmails();
    if (isAdmin) {
      fetchSettings();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (selectedEmail) {
      fetchInbox(selectedEmail.id);
      const interval = setInterval(() => fetchInbox(selectedEmail.id), 30000);
      return () => clearInterval(interval);
    }
  }, [selectedEmail]);

  const handleCreateEmail = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(`${API_URL}/api/temp-email/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ prefix: prefix || null })
      });

      if (res.ok) {
        toast.success("Email создан");
        fetchEmails();
        setShowCreateModal(false);
        setPrefix("");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Ошибка создания");
      }
    } catch (err) {
      toast.error("Ошибка сети");
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(`${API_URL}/api/temp-email/settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        toast.success("Настройки сохранены");
        setShowSettingsModal(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Ошибка сохранения");
      }
    } catch (err) {
      toast.error("Ошибка сети");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Скопировано");
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="temp-email-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Mail className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Временная почта</h1>
            <p className="text-sm text-slate-500">{emails.length} адресов</p>
          </div>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => setShowSettingsModal(true)} data-testid="email-settings-btn">
              <Settings className="w-4 h-4 mr-2" />
              Настройки
            </Button>
          )}
          <Button onClick={() => setShowCreateModal(true)} data-testid="create-email-btn">
            <Plus className="w-4 h-4 mr-2" />
            Создать
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Email List */}
        <div className="lg:col-span-1 space-y-3">
          {emails.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <Mail className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Нет временных адресов</p>
              <Button size="sm" className="mt-3" onClick={() => setShowCreateModal(true)}>
                Создать первый
              </Button>
            </div>
          ) : (
            emails.map(email => (
              <button
                key={email.id}
                onClick={() => setSelectedEmail(email)}
                className={`w-full bg-white rounded-xl border p-4 text-left hover:shadow-sm transition-all ${
                  selectedEmail?.id === email.id ? 'border-purple-500 ring-2 ring-purple-100' : 'border-slate-200'
                }`}
                data-testid={`email-item-${email.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-800 truncate flex-1">{email.email}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); copyToClipboard(email.email); }}
                    className="p-1 text-slate-400 hover:text-slate-600"
                    title="Копировать"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Clock className="w-3 h-3" />
                  {isExpired(email.expires_at) ? (
                    <span className="text-red-500">Истёк</span>
                  ) : (
                    <span>До {new Date(email.expires_at).toLocaleString("ru-RU")}</span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Inbox */}
        <div className="lg:col-span-2">
          {selectedEmail ? (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-800">{selectedEmail.email}</h3>
                  <p className="text-xs text-slate-500">Входящие сообщения</p>
                </div>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => fetchInbox(selectedEmail.id)}
                  disabled={inboxLoading}
                >
                  <RefreshCw className={`w-4 h-4 ${inboxLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                {inbox.length === 0 ? (
                  <div className="p-8 text-center">
                    <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Нет входящих писем</p>
                    <p className="text-xs text-slate-400 mt-1">Письма появятся здесь автоматически</p>
                  </div>
                ) : (
                  inbox.map(msg => (
                    <div key={msg.id} className="p-4 hover:bg-slate-50" data-testid={`inbox-message-${msg.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-slate-800">{msg.subject || "(Без темы)"}</p>
                          <p className="text-sm text-slate-500">{msg.from_email}</p>
                        </div>
                        <span className="text-xs text-slate-400">
                          {new Date(msg.received_at).toLocaleString("ru-RU")}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 whitespace-pre-wrap">{msg.body}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <Inbox className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Выберите email для просмотра входящих</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800">Создать временный email</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1 text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateEmail} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Префикс (опционально)
                </label>
                <Input
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value)}
                  placeholder="myemail"
                  data-testid="input-email-prefix"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Оставьте пустым для случайного адреса
                </p>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                  Отмена
                </Button>
                <Button type="submit" className="flex-1" data-testid="submit-create-email">
                  Создать
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Settings Modal (Admin only) */}
      {showSettingsModal && isAdmin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSettingsModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800">Настройки почтового сервера</h2>
              <button onClick={() => setShowSettingsModal(false)} className="p-1 text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSaveSettings} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">SMTP Host</label>
                  <Input
                    value={settings.smtp_host}
                    onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                    placeholder="smtp.example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">SMTP Port</label>
                  <Input
                    type="number"
                    value={settings.smtp_port}
                    onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) })}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">IMAP Host</label>
                  <Input
                    value={settings.imap_host}
                    onChange={(e) => setSettings({ ...settings, imap_host: e.target.value })}
                    placeholder="imap.example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">IMAP Port</label>
                  <Input
                    type="number"
                    value={settings.imap_port}
                    onChange={(e) => setSettings({ ...settings, imap_port: parseInt(e.target.value) })}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Домен</label>
                <Input
                  value={settings.domain}
                  onChange={(e) => setSettings({ ...settings, domain: e.target.value })}
                  placeholder="temp.example.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Логин</label>
                <Input
                  value={settings.username}
                  onChange={(e) => setSettings({ ...settings, username: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Пароль</label>
                <Input
                  type="password"
                  value={settings.password}
                  onChange={(e) => setSettings({ ...settings, password: e.target.value })}
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowSettingsModal(false)} className="flex-1">
                  Отмена
                </Button>
                <Button type="submit" className="flex-1" data-testid="submit-email-settings">
                  Сохранить
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
